import axios from 'axios';
import config from '@/config/config';
import notificationsHelper from '@satellite/helpers/notifications';
import { upperFirst } from 'lodash';
import { databaseActions, isSAMLWhitelistedURL, t } from '@nova/core';
import { isObject, isUUID } from 'class-validator';
import { LAST_CRUD_ACTION_KEY } from '@satellite/plugins/util';
import * as pjson from '../../../../package.json';

// Optional integration points for non-Vuex apps:
//   options = {
//     logout?: () => void,
//   }
// TODO: Refactor so it lives in Vue instance rather than window?
class AxiosPlugin {
  static init(redirect401, uiApplicationName, app, options = {}) {
    function setGlobalLoading(val) {
      app.config.globalProperties.$globalLoading = val;
    }

    function notify(message, status, options = {}) {
      options?.notify
        ? options.notify(message, status)
        : app.config.globalProperties.notify(message, status, options);
    }

    function logout() {
      options?.logout
        ? options.logout()
        : app.config.globalProperties.$store.dispatch('Auth/logout');
    }

    window.axios = axios;
    globalThis.axios = axios; // Added for TS support to allow axios.* instead of Window.axios.*
    const axiosInstance = window.axios;
    axiosInstance.defaults.baseURL = config.neutron_url;
    axiosInstance.defaults.headers['X-UI-AppName'] = uiApplicationName;
    axiosInstance.defaults.headers['X-UI-Version'] = pjson?.version;

    axiosInstance.defaults.paramsSerializer = params =>
      Object.keys(params)
        .filter(key => params[key] !== undefined)
        .map(key => {
          if (isObject(params[key])) {
            params[key] = JSON.stringify(params[key]);
          }
          if (Array.isArray(params[key])) {
            return params[key].map(v => `${key}[]=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(params[key])}`;
        })
        .join('&');

    axiosInstance.interceptors.request.use(config => {
      // app.$validator.clear();
      setGlobalLoading(true);
      return config;
    });

    // Response Interception
    axiosInstance.interceptors.response.use(
      response => {
        if (isObject(response?.data)) {
          const { entity, action, data } = response.data;

          const formattedEntityName = notificationsHelper.getEntityTerm(response.data);
          const crudAction = databaseActions[action]?.pastTense;

          // Global notification for succeeded WRITE actions
          if (formattedEntityName && crudAction && !response.config?.suppressNotification) {
            let message = `${formattedEntityName} has been ${crudAction}`;
            notify(message, 'success');
          }

          // Store the last operation at session storage
          // So we can ignore the subspace message to avoid double requests
          if (isUUID(data?.id) && ['delete', 'create', 'update'].includes(action)) {
            sessionStorage.setItem(LAST_CRUD_ACTION_KEY, `${action}-${entity}:${data?.id}`);
          }
        }
        setGlobalLoading(false);
        return response;
      },
      error => {
        setGlobalLoading(false);

        // we don't want to show a notification if the request was aborted
        if (error.name === 'CanceledError') {
          return;
        }

        // The request has returned no response, we have no context
        const hasErrorMessage = Boolean(error.response?.data?.message);
        let errorMessage = t('notifications.messages.networkError');

        if (hasErrorMessage) {
          errorMessage = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
        }

        errorMessage = upperFirst(errorMessage);
        if (error.response?.status === 401) {
          const logoutRedirectUrl = localStorage.getItem('logout_redirect_url');
          if (logoutRedirectUrl && isSAMLWhitelistedURL(logoutRedirectUrl)) {
            window.location.replace(logoutRedirectUrl);
            return;
          }

          if (redirect401) {
            const url = window.location.href;
            const path = new URL(url).pathname;
            logout();
            if (path !== redirect401) {
              window.location.replace(
                `${redirect401}?${window.location.pathname}&errorMessage=${errorMessage}`
              );
              return;
            }
          }
        }

        if (!error.config?.suppressNotification) {
          notify(errorMessage, 'error', {
            title:
              notificationsHelper.getNotificationTitleByRespCode(error.response?.data?.errorCode) ??
              t('global.basicTerms.error')
          });
        }

        return Promise.reject(error);
      }
    );
  }

  static setAccessToken(token) {
    window.axios.defaults.headers.Authorization = token ? `Bearer ${token}` : undefined;
  }
}

export default AxiosPlugin;
